import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, normalizeClass as _normalizeClass, createVNode as _createVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "notifications-modal__tabs" }
const _hoisted_2 = ["disabled"]
const _hoisted_3 = { class: "notifications-modal__tab-counter" }
const _hoisted_4 = ["disabled"]
const _hoisted_5 = {
  type: "button",
  class: "notifications-modal__move-all"
}
const _hoisted_6 = { class: "notifications-modal__body" }
const _hoisted_7 = {
  key: 0,
  class: "notifications-modal__divider"
}
const _hoisted_8 = {
  key: 1,
  class: "notifications-modal__sentinel",
  "aria-hidden": "true"
}
const _hoisted_9 = {
  key: 0,
  class: "notifications-modal__loader-more"
}
const _hoisted_10 = {
  key: 2,
  class: "notifications-modal__empty"
}

import {
	computed,
	onBeforeUnmount,
	onBeforeUpdate,
	ref,
	watch
} from "vue"
import BaseModal from "@/components/common/modal/BaseModal.vue"
import AppLoader from "@/components/common/AppLoader.vue"
import AppIcon from "@/components/ui-kit/icon/AppIcon.vue"
import AppResizableDropdown from "@/components/ui-kit/dropdown/AppResizableDropdown.vue"
import NotificationItem from "./NotificationItem.vue"
import NotificationMoveAllConfirm from "./NotificationMoveAllConfirm.vue"
import NotificationUndoOverlay from "./NotificationUndoOverlay.vue"
import { headerStore } from "@/store/header"
import { useModalEscape } from "@/composables/useModalEscape"


export default /*@__PURE__*/_defineComponent({
  __name: 'HeaderNotificationsModal',
  setup(__props, { expose: __expose }) {

const store = headerStore()
const $content = ref<HTMLElement | null>(null)
const $list = ref<HTMLElement | null>(null)
const $modal = ref<typeof BaseModal | null>(null)
const $moveAllConfirm = ref<typeof AppResizableDropdown | null>(null)
const $moveAllUndo = ref<InstanceType<typeof NotificationUndoOverlay> | null>(null)
const tab = ref<'active' | 'history'>('active')
const loading = ref(false)
const moveAllPending = ref(false)

const { activate: activateEsc, deactivate: deactivateEsc } = useModalEscape({
	content: $content,
	close: () => $modal.value?.onClose()
})

let itemRefs: any[] = []

onBeforeUpdate(() => {
	itemRefs = []
})

const setItemRef = (el: any) => {
	if (el) {
		itemRefs.push(el)
	}
}

const currentList = computed(() =>
	tab.value === 'active' ? store.activeNotifications : store.historyNotifications
)

const activeCount = computed(() => store.notificationCount.JobCount)

const loadingMore = computed(() =>
	tab.value === 'active' ? store.activeLoadingMore : store.historyLoadingMore
)

const sentinelIndex = computed(() => Math.max(0, currentList.value.length - 5))

let sentinelObserver: IntersectionObserver | null = null

const loadMore = () => {
	if (tab.value === 'active') {
		store.loadMoreActiveNotifications()
	} else {
		store.loadMoreHistoryNotifications()
	}
}

const observeSentinel = () => {
	sentinelObserver?.disconnect()
	sentinelObserver = null

	const root = $list.value
	const sentinel = root?.querySelector('.notifications-modal__sentinel')
	if (!root || !sentinel) {
		return
	}

	sentinelObserver = new IntersectionObserver((entries) => {
		if (entries[0]?.isIntersecting) {
			loadMore()
		}
	}, {
		root,
		threshold: 0,
	})
	sentinelObserver.observe(sentinel)
}

watch([$list, currentList, loading, loadingMore], observeSentinel, { flush: 'post' })

onBeforeUnmount(() => {
	sentinelObserver?.disconnect()
	sentinelObserver = null
})

const canMoveAll = computed(() =>
	tab.value === 'active'
	&& store.activeNotifications.length > 0
	&& !moveAllPending.value
)

const loadCurrentTab = async () => {
	loading.value = true
	try {
		if (tab.value === 'active') {
			await store.loadActiveNotifications()
		} else {
			await store.loadHistoryNotifications()
		}
	} finally {
		loading.value = false
	}
}

const setTab = async (value: 'active' | 'history') => {
	tab.value = value
	await loadCurrentTab()
}

const onMoveToHistory = async (id: number) => {
	await store.moveNotificationToHistory(id)
}

const onNavigate = () => {
	$modal.value?.onClose()
}

const onConfirmMoveAll = () => {
	$moveAllConfirm.value?.close()
	moveAllPending.value = true
}

const onCancelMoveAll = () => {
	moveAllPending.value = false
}

const onMoveAllFinish = async () => {
	moveAllPending.value = false
	loading.value = true
	try {
		await store.moveAllToHistory()
	} finally {
		loading.value = false
	}
}

const open = async () => {
	moveAllPending.value = false
	$modal.value?.onOpen()
	activateEsc()
	tab.value = 'active'
	await loadCurrentTab()
}

const onClose = () => {
	deactivateEsc()
	if (moveAllPending.value) {
		$moveAllUndo.value?.flush()
		return
	}
	itemRefs.forEach(item => item?.flushPendingMove?.())
}

__expose({ open })

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(BaseModal, {
    ref_key: "$modal",
    ref: $modal,
    "close-by-click-outside": "",
    "fullscreen-on-mobile": "",
    title: _ctx.$t('notifications_title'),
    width: "600px",
    height: "80vh",
    onOnClose: onClose
  }, {
    default: _withCtx(() => [
      _createElementVNode("div", {
        ref_key: "$content",
        ref: $content,
        class: "notifications-modal"
      }, [
        _createElementVNode("div", _hoisted_1, [
          _createElementVNode("button", {
            type: "button",
            class: _normalizeClass(["notifications-modal__tab", { 'notifications-modal__tab--active': tab.value === 'active' }]),
            disabled: moveAllPending.value,
            onClick: _cache[0] || (_cache[0] = ($event: any) => (setTab('active')))
          }, [
            _createTextVNode(_toDisplayString(_ctx.$t('notifications_new')) + " ", 1),
            _createElementVNode("span", _hoisted_3, _toDisplayString(activeCount.value), 1)
          ], 10, _hoisted_2),
          _createElementVNode("button", {
            type: "button",
            class: _normalizeClass(["notifications-modal__tab", { 'notifications-modal__tab--active': tab.value === 'history' }]),
            disabled: moveAllPending.value,
            onClick: _cache[1] || (_cache[1] = ($event: any) => (setTab('history')))
          }, _toDisplayString(_ctx.$t('notifications_history')), 11, _hoisted_4),
          (canMoveAll.value)
            ? (_openBlock(), _createBlock(AppResizableDropdown, {
                key: 0,
                ref_key: "$moveAllConfirm",
                ref: $moveAllConfirm,
                class: "notifications-modal__move-all-wrap",
                position: "bottom",
                "max-width": 280,
                "content-z-index": 10002,
                "close-by-click-outside": "",
                "centered-on-mobile": ""
              }, {
                activator: _withCtx(() => [
                  _createElementVNode("button", _hoisted_5, [
                    _createTextVNode(_toDisplayString(_ctx.$t('notifications_move_all')) + " ", 1),
                    _createVNode(AppIcon, {
                      name: "rollback",
                      size: "18px",
                      class: "notifications-modal__move-all-icon",
                      style: { marginTop: '-2px' }
                    })
                  ])
                ]),
                default: _withCtx(() => [
                  _createVNode(NotificationMoveAllConfirm, {
                    onConfirm: onConfirmMoveAll,
                    onCancel: _cache[2] || (_cache[2] = ($event: any) => ($moveAllConfirm.value?.close()))
                  })
                ]),
                _: 1
              }, 512))
            : _createCommentVNode("", true)
        ]),
        _createElementVNode("div", _hoisted_6, [
          _createElementVNode("div", {
            ref_key: "$list",
            ref: $list,
            class: "notifications-modal__list"
          }, [
            (loading.value)
              ? (_openBlock(), _createBlock(AppLoader, { key: 0 }))
              : (currentList.value.length)
                ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(currentList.value, (n, index) => {
                      return (_openBlock(), _createElementBlock(_Fragment, {
                        key: n.Id
                      }, [
                        _createVNode(NotificationItem, {
                          ref_for: true,
                          ref: setItemRef,
                          notification: n,
                          "show-move-action": tab.value === 'active',
                          onMoveToHistory: onMoveToHistory,
                          onNavigate: onNavigate
                        }, null, 8, ["notification", "show-move-action"]),
                        (index + 1 < currentList.value.length)
                          ? (_openBlock(), _createElementBlock("div", _hoisted_7))
                          : _createCommentVNode("", true),
                        (index === sentinelIndex.value)
                          ? (_openBlock(), _createElementBlock("div", _hoisted_8))
                          : _createCommentVNode("", true)
                      ], 64))
                    }), 128)),
                    (loadingMore.value)
                      ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
                          _createVNode(AppLoader)
                        ]))
                      : _createCommentVNode("", true)
                  ], 64))
                : (_openBlock(), _createElementBlock("div", _hoisted_10, _toDisplayString(_ctx.$t('list_empty')), 1))
          ], 512),
          (moveAllPending.value)
            ? (_openBlock(), _createBlock(NotificationUndoOverlay, {
                key: 0,
                ref_key: "$moveAllUndo",
                ref: $moveAllUndo,
                style: { zIndex: 4 },
                text: _ctx.$t('notifications_moved_to_history'),
                onFinish: onMoveAllFinish,
                onCancel: onCancelMoveAll
              }, null, 8, ["text"]))
            : _createCommentVNode("", true)
        ])
      ], 512)
    ]),
    _: 1
  }, 8, ["title"]))
}
}

})